// Treasure Collect – "Turn cards into treasures": the before/after slider.
// Scrolling uncovers the display; once most of it is uncovered and the visitor pauses, it finishes on its own.
// Around it, fine gold dust drifts slowly through the section (the same dust as the five-layers animation); when the
// display is complete the five-layers band of light passes over it (and again every few seconds), little glints sparkle
// round its edges and a few specks rise from it. Dragging takes over until the visitor scrolls on.
(() => {
  if (window.tcScrollRevealLoaded) return;
  window.tcScrollRevealLoaded = true;
  const instances = new Map();
  const clamp = (v, a = 0, b = 1) => Math.max(a, Math.min(b, v));

  // one soft gold speck, drawn once and reused for every particle
  let sprite = null;
  const getSprite = () => {
    if (sprite) return sprite;
    sprite = document.createElement('canvas');
    sprite.width = sprite.height = 32;
    const c = sprite.getContext('2d');
    const g = c.createRadialGradient(16, 16, 0, 16, 16, 16);
    g.addColorStop(0, 'rgba(255,214,120,1)');
    g.addColorStop(0.16, 'rgba(255,199,82,1)');
    g.addColorStop(0.5, 'rgba(255,190,70,.35)');
    g.addColorStop(1, 'rgba(255,190,70,0)');
    c.fillStyle = g;
    c.fillRect(0, 0, 32, 32);
    return sprite;
  };

  function init(root) {
    if (instances.has(root)) return;
    const track = root.querySelector('.tc-ba__scroll-track');
    const scene = root.querySelector('.tc-ba__scene');
    if (!track || !scene) return;
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const events = new AbortController();
    const on = (el, type, fn, opts = {}) => el && el.addEventListener(type, fn, { ...opts, signal: events.signal });
    const container = root.querySelector('.tc-ba__container');
    const handle = root.querySelector('.tc-ba__handle');
    let frame = 0;
    let visible = true;
    let current = 0;
    let manual = false;      // the visitor is driving the slider (until they scroll on)
    let manualY = 0;
    let finishing = 0;       // rAF of the automatic finish
    let finished = false;    // reached the end: scrolling further down keeps it there
    let settle = 0;

    // ---------- the title: the words rise out of a soft blur one after another ----------
    const title = root.querySelector('.tc-ba__title');
    const gold = root.querySelector('.tc-ba__title-gold');
    if (title && !title.dataset.split) {
      title.dataset.split = '1';
      let w = 0;
      const wrap = (node) => {
        [...node.childNodes].forEach((child) => {
          if (child.nodeType === 3) {
            const frag = document.createDocumentFragment();
            child.textContent.split(/(\s+)/).forEach((part) => {
              if (!part) return;
              if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
              const s = document.createElement('span');
              s.className = 'tc-ba__w';
              s.style.setProperty('--w', w++);
              s.textContent = part;
              frag.appendChild(s);
            });
            child.replaceWith(frag);
          } else if (child === gold) {
            child.classList.add('tc-ba__w');
            child.style.setProperty('--w', w++);
          } else if (child.nodeType === 1) wrap(child);
        });
      };
      wrap(title);
      title.classList.add('is-split');
    }
    // ---------- the BEFORE / AFTER tags: letters that rise one by one (the plain text stays for screen readers) ----------
    root.querySelectorAll('.tc-ba__tag').forEach((tag) => {
      const t = tag.querySelector('.tc-ba__tag-t');
      if (!t || tag.classList.contains('is-split')) return;
      const text = t.textContent.trim();
      const sr = document.createElement('span');
      sr.className = 'visually-hidden';
      sr.textContent = text;
      t.textContent = '';
      t.setAttribute('aria-hidden', 'true');
      [...text].forEach((ch, i) => {
        const l = document.createElement('span');
        l.className = 'tc-ba__tl';
        l.style.setProperty('--l', i);
        l.textContent = ch;
        t.appendChild(l);
      });
      tag.appendChild(sr);
      tag.classList.add('is-split');
    });
    new IntersectionObserver((entries, obs) => {
      if (entries[0].isIntersecting) { root.classList.add('is-inview'); obs.disconnect(); }
    }, { threshold: 0.25 }).observe(scene);

    // ---------- gold dust drifting through the whole section ----------
    const dust = root.querySelector('.tc-ba__dust');
    const light = root.classList.contains('tc-ba--light');
    const ctx = dust && dust.getContext ? dust.getContext('2d') : null;
    let W = 1, H = 1, motes = [], rising = [], running = false, last = 0, onScreen = false;
    const seedMote = (m, fresh) => {
      m.x = Math.random() * W;
      m.z = Math.random();                              // size, brightness and speed come from depth
      m.d = 2.2 + m.z * 3.6;                            // diameter (px)
      m.v = H / (26 + (1 - m.z) * 34);                  // crosses the section in 26–60 s
      m.k = fresh ? Math.random() : 0;                  // where it is in its journey (0 bottom, 1 top)
      m.s = Math.random() * 6.28;
      return m;
    };
    const sizeDust = () => {
      if (!ctx) return;
      const r = root.getBoundingClientRect(), dpr = Math.min(window.devicePixelRatio || 1, 2);
      W = Math.max(1, r.width); H = Math.max(1, r.height);
      dust.width = Math.round(W * dpr); dust.height = Math.round(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const want = Math.round(clamp((W * H) / 16000, 22, 64));
      while (motes.length < want) motes.push(seedMote({}, true));
      motes.length = want;
    };
    const tick = (now) => {
      const dt = last ? Math.min(0.05, (now - last) / 1000) : 0.016;
      last = now;
      ctx.clearRect(0, 0, W, H);
      const img = getSprite();
      // gold dust adds light on dark backgrounds; on light ones it is painted as is, a little softer
      ctx.globalCompositeOperation = light ? 'source-over' : 'lighter';
      motes.forEach((m) => {
        m.k += (m.v * dt) / H;
        if (m.k >= 1) seedMote(m, false);
        const y = H * (1 - m.k);
        const x = m.x + Math.sin(now / 2600 + m.s) * 6;
        const a = Math.sin(m.k * Math.PI) * (0.25 + 0.75 * m.z) * 0.8;
        ctx.globalAlpha = a;
        ctx.drawImage(img, x - m.d, y - m.d, m.d * 2, m.d * 2);
      });
      rising = rising.filter((p) => {
        p.t += dt;
        if (p.t > p.life) return false;
        p.y -= p.v * dt;
        const k = p.t / p.life;
        ctx.globalAlpha = Math.sin(k * Math.PI) * p.a;
        const x = p.x + Math.sin(now / 900 + p.s) * 4;
        ctx.drawImage(img, x - p.d, p.y - p.d, p.d * 2, p.d * 2);
        return true;
      });
      ctx.globalAlpha = 1;
      if (onScreen && !document.hidden) requestAnimationFrame(tick); else { running = false; last = 0; }
    };
    const runDust = () => { if (!ctx || running || motion.matches) return; running = true; last = 0; requestAnimationFrame(tick); };
    // a few brighter specks rise out of the finished display
    const rise = () => {
      if (!ctx || motion.matches) return;
      const s = root.getBoundingClientRect(), c = container.getBoundingClientRect();
      for (let i = 0; i < 18; i++) {
        rising.push({
          x: c.left - s.left + c.width * (0.08 + Math.random() * 0.84),
          y: c.top - s.top + c.height * (0.35 + Math.random() * 0.6),
          v: 24 + Math.random() * 34, d: 2.4 + Math.random() * 3, a: 0.55 + Math.random() * 0.4,
          t: -Math.random() * 0.9, life: 2.6 + Math.random() * 1.8, s: Math.random() * 6.28,
        });
      }
      runDust();
    };
    if (ctx) {
      new IntersectionObserver((entries) => { onScreen = entries[0].isIntersecting; if (onScreen) { sizeDust(); runDust(); } }).observe(root);
      new ResizeObserver(sizeDust).observe(root);
      on(document, 'visibilitychange', () => { if (!document.hidden && onScreen) runDust(); });
    }

    // ---------- the reveal ----------
    let revealed = false;
    function setReveal(value) {
      const pct = clamp(value, 0, 100);
      current = pct;
      root.style.setProperty('--reveal', pct.toFixed(2));
      if (handle) handle.setAttribute('aria-valuenow', Math.round(pct));
      root.classList.toggle('is-after', pct >= 50);
      root.classList.toggle('is-start', pct <= 0.5);
      const done = pct >= 99.5;
      root.classList.toggle('is-revealed', done);
      if (done !== revealed) {
        revealed = done;
        if (done) rise();
      }
    }
    const finish = () => {
      if (finishing || current >= 99.5) return;
      const from = current, t0 = performance.now(), dur = 700;
      const step = (now) => {
        const k = clamp((now - t0) / dur), e = 1 - Math.pow(1 - k, 3);
        setReveal(from + (100 - from) * e);
        if (k < 1) finishing = requestAnimationFrame(step);
        else { finishing = 0; finished = true; }
      };
      finishing = requestAnimationFrame(step);
    };
    function update() {
      frame = 0;
      if (manual) {
        // the visitor dragged it: scrolling on (either way) gives control back to the page
        if (Math.abs(window.scrollY - manualY) < 120) return;
        manual = false;
      }
      if (motion.matches) {
        root.classList.remove('is-scroll-enabled');
        setReveal(100);
        return;
      }
      root.classList.add('is-scroll-enabled');
      const rect = container.getBoundingClientRect();
      const vh = document.documentElement.clientHeight;
      // complete once the display's top passes 36% of the screen
      const p = clamp((vh * 0.86 - rect.top) / (vh * 0.5)) * 100;
      if (finished || finishing) {
        if (p > 40) return;                   // stays complete while the visitor reads on
        cancelAnimationFrame(finishing); finishing = 0; finished = false;
      }
      setReveal(p);
      if (p >= 99.5) { finished = true; return; }
      // most of it uncovered and the visitor pauses: it finishes on its own
      clearTimeout(settle);
      if (p >= 55) settle = setTimeout(finish, 200);
    }
    function fromPointer(event) {
      const box = container.getBoundingClientRect();
      setReveal(((event.clientX - box.left) / box.width) * 100);
    }
    let dragging = false;
    const takeOver = () => {
      manual = true; manualY = window.scrollY;
      cancelAnimationFrame(finishing); finishing = 0; finished = false; clearTimeout(settle);
      root.classList.add('is-touched');
    };
    if (container) {
      on(container, 'pointerdown', (event) => {
        if (event.button !== 0) return;
        takeOver();
        dragging = true;
        root.classList.add('is-dragging');
        container.setPointerCapture(event.pointerId);
        fromPointer(event);
      });
      on(container, 'pointermove', (event) => { if (dragging) fromPointer(event); });
      const stop = () => { dragging = false; root.classList.remove('is-dragging'); };
      on(container, 'pointerup', stop);
      on(container, 'pointercancel', stop);
    }
    if (handle) {
      on(handle, 'keydown', (event) => {
        const steps = { ArrowLeft: -5, ArrowDown: -5, ArrowRight: 5, ArrowUp: 5 };
        let next = null;
        if (event.key in steps) next = current + steps[event.key];
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = 100;
        if (next === null) return;
        event.preventDefault();
        takeOver();
        setReveal(next);
      });
    }
    function schedule() {
      if (!frame) frame = requestAnimationFrame(update);
    }
    const observer = new IntersectionObserver((entries) => {
      visible = entries[0].isIntersecting;
      if (visible) schedule();
    }, { rootMargin: '100% 0px' });
    observer.observe(root);
    on(window, 'scroll', () => { if (visible) schedule(); }, { passive: true });
    on(window, 'resize', schedule, { passive: true });
    on(motion, 'change', schedule);
    const resize = new ResizeObserver(schedule);
    resize.observe(scene);
    update();
    instances.set(root, () => {
      events.abort();
      observer.disconnect();
      resize.disconnect();
      clearTimeout(settle);
      cancelAnimationFrame(finishing);
      if (frame) cancelAnimationFrame(frame);
      motes = []; rising = []; onScreen = false;
      instances.delete(root);
    });
  }
  function scan(scope) {
    if (scope.matches?.('[data-scroll-reveal]')) init(scope);
    scope.querySelectorAll('[data-scroll-reveal]').forEach(init);
  }
  scan(document);
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    instances.forEach((cleanup, root) => { if (event.target.contains(root)) cleanup(); });
  });
})();
